import { FileDown, RefreshCw } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { TransactionType } from '../../types/transactions'
import { TabsHeader, TabsHeaderActions, TabsHeaderTitle } from '../ui'

export enum FilterStatus {
  Completed = 'COMPLETED',
  Awaiting = 'AWAITING',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  Approved = 'APPROVED',
}

export interface TransactionsHeaderProps {
  selectedStatus: string | null
  selectedType: TransactionType | null
  isFlagged?: boolean | null
  onFilter: (
    status: FilterStatus | null,
    type: TransactionType | null,
    flagged?: boolean | null,
  ) => void
  onExportCSV?: () => void
  onRefresh?: () => void
  isRefreshing?: boolean
  showExportButton?: boolean
  showRefreshButton?: boolean
  headerSearchInput?: React.ReactNode
}

const activeClassName = (condition: boolean) => {
  if (condition) {
    return 'border-main text-main-foreground bg-main'
  }
  return 'bg-white text-card-text border-card-border hover:bg-gray-100'
}

const BadgeWrapper = ({
  children,
  onClick,
  isActive,
}: {
  children: React.ReactNode
  onClick: () => void
  isActive: boolean
}) => {
  return (
    <Badge
      variant="outline"
      className={`cursor-pointer px-2.5 py-2 ${activeClassName(isActive)}`}
      onClick={onClick}
    >
      <span className="leading-none">{children}</span>
    </Badge>
  )
}

const getFilterValue = (
  selectedStatus: string | null,
  selectedType: TransactionType | null,
  isFlagged: boolean | null | undefined,
): string => {
  if (isFlagged === true) return 'flagged'
  if (selectedStatus === FilterStatus.Completed) return 'completed'
  if (selectedStatus === FilterStatus.Awaiting) return 'awaiting'
  if (selectedStatus === FilterStatus.Pending) return 'pending'
  if (selectedStatus === FilterStatus.Rejected) return 'rejected'
  if (selectedType === TransactionType.Trade) return 'swap'
  if (selectedType === TransactionType.Deposit) return 'deposits'
  return 'all'
}

export function TransactionsHeader({
  selectedStatus,
  selectedType,
  isFlagged,
  onFilter,
  onExportCSV,
  onRefresh,
  isRefreshing = false,
  showExportButton = false,
  showRefreshButton = false,
  headerSearchInput,
}: TransactionsHeaderProps) {
  return (
    <div className="space-y-4">
      <TabsHeader className="flex-wrap gap-2 md:gap-2 lg:gap-2">
        <div className="flex items-center justify-between md:justify-start flex-row gap-2 md:gap-2 lg:gap-6 flex-wrap">
          <TabsHeaderTitle>Transactions</TabsHeaderTitle>

          <div className="flex items-center justify-between">
            {/* Desktop: Show badges */}
            <div className="hidden md:flex items-center gap-2 flex-wrap mb-2 lg:mb-0">
              <BadgeWrapper
                onClick={() => onFilter(null, null, null)}
                isActive={!selectedType && !selectedStatus && !isFlagged}
              >
                All
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => {
                  const newStatus =
                    selectedStatus === FilterStatus.Completed ? null : FilterStatus.Completed
                  onFilter(newStatus, null, null)
                }}
                isActive={selectedStatus === FilterStatus.Completed}
              >
                Completed
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => {
                  const newStatus =
                    selectedStatus === FilterStatus.Awaiting ? null : FilterStatus.Awaiting
                  onFilter(newStatus, null, null)
                }}
                isActive={selectedStatus === FilterStatus.Awaiting}
              >
                Awaiting
              </BadgeWrapper>

              <BadgeWrapper
                onClick={() => {
                  const newType =
                    selectedType === TransactionType.Trade ? null : TransactionType.Trade
                  onFilter(null, newType, null)
                }}
                isActive={selectedType === TransactionType.Trade}
              >
                Swap
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => {
                  const newType =
                    selectedType === TransactionType.Deposit ? null : TransactionType.Deposit
                  onFilter(null, newType, null)
                }}
                isActive={selectedType === TransactionType.Deposit}
              >
                Deposits
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => {
                  const newStatus =
                    selectedStatus === FilterStatus.Pending ? null : FilterStatus.Pending
                  onFilter(newStatus, null, null)
                }}
                isActive={selectedStatus === FilterStatus.Pending}
              >
                Pending
              </BadgeWrapper>
              <BadgeWrapper
                isActive={isFlagged === true}
                onClick={() => {
                  const newFlagged = isFlagged === true ? null : true
                  onFilter(null, null, newFlagged)
                }}
              >
                Flagged
              </BadgeWrapper>
              <BadgeWrapper
                onClick={() => {
                  const newStatus =
                    selectedStatus === FilterStatus.Rejected ? null : FilterStatus.Rejected
                  onFilter(newStatus, null, null)
                }}
                isActive={selectedStatus === FilterStatus.Rejected}
              >
                Rejected
              </BadgeWrapper>
            </div>

            {/* Mobile: Show dropdown */}
            <div className="md:hidden">
              <Select
                value={getFilterValue(selectedStatus, selectedType, isFlagged)}
                onValueChange={(value) => {
                  switch (value) {
                    case 'all':
                      onFilter(null, null, null)
                      break
                    case 'completed':
                      onFilter(FilterStatus.Completed, null, null)
                      break
                    case 'awaiting':
                      onFilter(FilterStatus.Awaiting, null, null)
                      break
                    case 'pending':
                      onFilter(FilterStatus.Pending, null, null)
                      break
                    case 'rejected':
                      onFilter(FilterStatus.Rejected, null, null)
                      break
                    case 'swap':
                      onFilter(null, TransactionType.Trade, null)
                      break
                    case 'deposits':
                      onFilter(null, TransactionType.Deposit, null)
                      break
                    case 'flagged':
                      onFilter(null, null, true)
                      break
                  }
                }}
              >
                <SelectTrigger className="w-[140px] bg-white">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="awaiting">Awaiting</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="swap">Swap</SelectItem>
                  <SelectItem value="deposits">Deposits</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <TabsHeaderActions>
          {headerSearchInput}
          {showRefreshButton && onRefresh && (
            <Button variant="outline" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
          {showExportButton && onExportCSV && (
            <Button variant="main_action" onClick={onExportCSV}>
              <FileDown className="h-4 w-4" />
              Export CSV
            </Button>
          )}
        </TabsHeaderActions>
      </TabsHeader>
    </div>
  )
}
